import DataStructure, { sanitize } from "./DataStructure";
import Address from "./Address";
import COLORS from "../colors.js";
import { deepFreeze, timestampToDate } from "../util/misc";
import { sortBy } from "lodash";

// The customer's copy of a project, written by functions/customerMirrors.js
// at c/{customerID}/projects/{projectID}. It carries no totals, costs or
// employee IDs -- see the whitelists in functions/customerMirrorBuilders.js.
export const CUSTOMER_PROJECT_FIELDS = {
  name: "",
  type: "",
  address: null,
  soldDate: null,
  archived: false,
  servicePhase: "none",
  sales: {},

  created: null,
  modified: null,
};
deepFreeze(CUSTOMER_PROJECT_FIELDS);

// Where a job stands, in the customer's terms. The sale statuses
// (SaleStatus.js) are our pipeline -- awaiting bill, ready to pay, critical
// issues -- so they collapse into the few stages a customer follows.
export const CUSTOMER_PROJECT_PHASES = {
  starting: {
    order: 0,
    label: "Getting started",
    icon: "new_home",
    color: "info",
  },
  ordered: {
    order: 1,
    label: "On order",
    icon: "order",
    color: "info",
  },
  preparing: {
    order: 2,
    label: "Preparing for delivery",
    icon: "warehouse",
    color: "warning",
  },
  scheduled: {
    order: 3,
    label: "Scheduled",
    icon: "calendar",
    color: "warning",
  },
  installing: {
    order: 4,
    label: "Installing",
    icon: "installation",
    color: "warning",
  },
  service: {
    order: 5,
    label: "In service",
    icon: "service",
    color: "warning",
  },
  complete: {
    order: 6,
    label: "Complete",
    icon: "check_circle",
    color: "positive",
  },
};
deepFreeze(CUSTOMER_PROJECT_PHASES);

const SALE_STATUS_PHASES = {
  changeOrder: "starting",
  downPayment: "starting",
  downPaymentRequested: "starting",
  ordering: "starting",
  ack: "ordered",
  production: "ordered",
  qc: "preparing",
  issuesCritical: "preparing",
  scheduling: "preparing",
  scheduled: "scheduled",
  pickup: "scheduled",
  install: "scheduled",
  installing: "installing",
  service: "service",
  issues: "service",
  // Billing and collection are ours to chase; the job itself is done.
  billing: "complete",
  paying: "complete",
  collecting: "complete",
  complete: "complete",
};

export const salePhase = (status) => SALE_STATUS_PHASES[status] || "starting";

export default class CustomerProject extends DataStructure {
  constructor(customerID, id, data = {}) {
    data = sanitize(data, CUSTOMER_PROJECT_FIELDS);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    super(data);

    this.customerID = customerID;
    this.id = id;
  }

  get address() {
    return this.data.address ? new Address(this.data.address) : null;
  }

  // The project's sales, oldest first, without the canceled ones.
  get sales() {
    return sortBy(
      Object.entries(this.data.sales)
        .filter(([, sale]) => sale && sale.status !== "canceled")
        .map(([id, sale]) => ({
          ...sale,
          id,
          latestAckShipDate: timestampToDate(sale.latestAckShipDate),
          phase: salePhase(sale.status),
        })),
      "id"
    );
  }

  // The earliest stage any of its sales is at, so a project reads Complete
  // only once all of them are.
  get phaseType() {
    const sales = this.sales;
    if (!sales.length) return "starting";
    return sortBy(
      sales.map((sale) => sale.phase),
      (phase) => CUSTOMER_PROJECT_PHASES[phase].order
    )[0];
  }

  get phase() {
    const type = CUSTOMER_PROJECT_PHASES[this.phaseType];
    return { ...type, ...COLORS[type.color], type: this.phaseType };
  }

  get isComplete() {
    return this.phaseType === "complete";
  }

  get brands() {
    const brands = new Set();
    this.sales.forEach((sale) =>
      Object.keys(sale.brandSeries || {}).forEach((brand) => brands.add(brand))
    );
    return [...brands].sort();
  }

  // The latest acknowledged ship date across the sales still on order, while
  // it is still ahead: a date that has passed says nothing useful about when
  // the material will come.
  get expectedShipDate() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates = this.sales
      .filter((sale) => sale.phase === "ordered" && sale.latestAckShipDate)
      .map((sale) => sale.latestAckShipDate);
    const latest = dates.length ? new Date(Math.max(...dates)) : null;
    return latest && latest >= today ? latest : null;
  }

  // The location of its most recent sale, for "Contact us" details.
  get location() {
    const sales = this.sales.filter((sale) => sale.location);
    return sales.length ? sales[sales.length - 1].location : null;
  }

  get icon() {
    return "project";
  }

  toString() {
    return (
      this.data.name || (this.address ? this.address.toString() : "") || this.id
    );
  }

  // Database

  get path() {
    return CustomerProject.getPath(this.customerID, this.id);
  }

  static getPath(customerID, id) {
    let path = `c/${customerID}/projects`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(project) {
    return project.data;
  }

  static fromFirestore(snapshot, options) {
    return new CustomerProject(
      snapshot.ref.parent.parent.id,
      snapshot.id,
      snapshot.data(options)
    );
  }
}
